import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, LogIn, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Selamat datang kembali!");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-[#064E3B]">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1559628233-100c798642d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative flex items-center gap-3 text-white">
          <div className="h-11 w-11 rounded-xl bg-[#D97706] grid place-items-center">
            <Leaf size={24} className="text-white" />
          </div>
          <span className="font-heading text-2xl font-extrabold tracking-tight">DigiDesa</span>
        </div>
        <div className="relative text-white">
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold leading-tight">
            Administrasi Desa<br />dalam Genggaman.
          </h1>
          <p className="mt-6 text-emerald-100/90 text-base max-w-md leading-relaxed">
            Kelola data wilayah, penduduk, surat-menyurat, dan pengumuman desa dalam satu
            sistem terpadu. Data wilayah otomatis dari sumber resmi.
          </p>
        </div>
        <div className="relative text-emerald-200/70 text-sm">
          Sumber data wilayah: wilayah.id
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-xl bg-[#064E3B] grid place-items-center">
              <Leaf size={24} className="text-white" />
            </div>
            <span className="font-heading text-2xl font-extrabold text-[#064E3B]">DigiDesa</span>
          </div>

          <h2 className="font-heading text-3xl font-bold text-slate-900">Masuk ke Sistem</h2>
          <p className="text-slate-500 mt-2">Silakan masuk menggunakan akun administrator desa.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Nama Pengguna</Label>
              <Input
                id="email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Masukkan nama pengguna"
                required
                data-testid="login-email-input"
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Kata Sandi</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input"
                className="h-12"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full h-12 bg-[#064E3B] hover:bg-[#043E2F] text-white text-base font-semibold rounded-xl transition-colors"
            >
              {busy ? <Loader2 className="animate-spin" size={20} /> : <><LogIn size={18} className="mr-2" /> Masuk</>}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
